import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Switch,
  FormControlLabel,
  Collapse
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DeleteSweep as DeleteSweepIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';

const SecurityManagement = ({ 
  blockedIps, 
  suspiciousIps, 
  whitelistedIps, 
  onRefresh,
  page = 0,
  rowsPerPage = 100,
  totalRows = 0,
  onPageChange,
  onRowsPerPageChange
}) => {
  const [selectedIps, setSelectedIps] = useState([]);
  const [expandedIp, setExpandedIp] = useState(null);
  const [ipLogs, setIpLogs] = useState({});
  const [blockingEnabled, setBlockingEnabled] = useState(() => {
    // Загружаем состояние из localStorage
    const saved = localStorage.getItem('blockingEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkBlockDialogOpen, setBulkBlockDialogOpen] = useState(false);
  
  const [editingIp, setEditingIp] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editBlockedBy, setEditBlockedBy] = useState('manual');
  
  const [newIps, setNewIps] = useState('');
  const [newReason, setNewReason] = useState('Manual block');
  
  const [bulkIps, setBulkIps] = useState('');
  const [bulkReason, setBulkReason] = useState('Bulk manual block');

  // Выбор/снятие выбора IP
  const handleSelectIp = (ip) => {
    setSelectedIps(prev => 
      prev.includes(ip) ? prev.filter(i => i !== ip) : [...prev, ip]
    );
  };

  const handleSelectAll = () => {
    if (selectedIps.length === blockedIps.length) {
      setSelectedIps([]);
    } else {
      setSelectedIps(blockedIps.map(item => item.ip));
    }
  };

  // Редактирование IP
  const handleOpenEdit = (ipData) => {
    setEditingIp(ipData.ip);
    setEditReason(ipData.reason || '');
    setEditBlockedBy(ipData.blocked_at || 'manual');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await monitoringAPI.updateBlockedIp(editingIp, editReason, editBlockedBy);
      setEditDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating IP:', error);
      alert('Ошибка при обновлении IP');
    }
  };

  // Добавление IP
  const handleAddIps = async () => {
    try {
      const ips = newIps.split('\n').map(ip => ip.trim()).filter(ip => ip);
      await monitoringAPI.bulkBlock(ips, newReason);
      setAddDialogOpen(false);
      setNewIps('');
      setNewReason('Manual block');
      onRefresh();
    } catch (error) {
      console.error('Error adding IPs:', error);
      alert('Ошибка при добавлении IP');
    }
  };

  // Массовая разблокировка
  const handleBulkUnblock = async () => {
    if (selectedIps.length === 0) {
      alert('Выберите IP для разблокировки');
      return;
    }
    
    if (window.confirm(`Разблокировать ${selectedIps.length} IP?`)) {
      try {
        await monitoringAPI.bulkUnblock(selectedIps);
        setSelectedIps([]);
        onRefresh();
      } catch (error) {
        console.error('Error unblocking IPs:', error);
        alert('Ошибка при разблокировке IP');
      }
    }
  };

  // Очистить весь список заблокированных IP
  const handleClearAllBlocked = async () => {
    if (window.confirm(`Вы уверены что хотите очистить ВСЕ заблокированные IP (${totalRows})? Это действие нельзя отменить!`)) {
      try {
        // Используем специальный endpoint для удаления всех IP из базы
        const result = await monitoringAPI.clearAllBlocked();
        setSelectedIps([]);
        onRefresh();
        alert(`Успешно удалено ${result.data.count} заблокированных IP`);
      } catch (error) {
        console.error('Error clearing blocked IPs:', error);
        alert('Ошибка при очистке списка');
      }
    }
  };

  // Переключение блокировки
  const handleToggleBlocking = (enabled) => {
    setBlockingEnabled(enabled);
    localStorage.setItem('blockingEnabled', JSON.stringify(enabled));
    // TODO: Здесь нужно добавить API вызов для включения/выключения блокировки на сервере
    console.log('Blocking enabled:', enabled);
  };

  // Загрузка логов для IP
  const handleExpandIp = async (ip) => {
    const newExpandedIp = expandedIp === ip ? null : ip;
    setExpandedIp(newExpandedIp);
    
    // Если разворачиваем и еще не загружали логи для этого IP
    if (newExpandedIp && !ipLogs[ip]) {
      try {
        const result = await monitoringAPI.getIpLogs(ip, 10);
        setIpLogs(prev => ({ ...prev, [ip]: result.data }));
      } catch (error) {
        console.error('Error loading IP logs:', error);
        setIpLogs(prev => ({ ...prev, [ip]: [] }));
      }
    }
  };

  return (
    <Box>
      {/* Рубильник блокировки */}
      <Box sx={{ mb: 2, p: 2, bgcolor: blockingEnabled ? 'error.light' : 'success.light', borderRadius: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={blockingEnabled}
              onChange={(e) => handleToggleBlocking(e.target.checked)}
              color={blockingEnabled ? "error" : "success"}
            />
          }
          label={
            <Typography variant="h6" color={blockingEnabled ? 'error.dark' : 'success.dark'}>
              {blockingEnabled ? '🔒 Блокировка ВКЛЮЧЕНА' : '🔓 Блокировка ВЫКЛЮЧЕНА'}
            </Typography>
          }
        />
        <Typography variant="caption" display="block" sx={{ ml: 6, mt: 0.5 }}>
          {blockingEnabled 
            ? 'Заблокированные IP не смогут получить доступ к API' 
            : 'Все IP имеют доступ к API (блокировки игнорируются)'}
        </Typography>
      </Box>

      {/* Кнопки управления */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Добавить IP
        </Button>
        
        {selectedIps.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleBulkUnblock}
          >
            Разблокировать выбранные ({selectedIps.length})
          </Button>
        )}

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearAllBlocked}
        >
          Очистить все ({totalRows})
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Всего заблокировано: {totalRows} IP
        </Typography>
      </Box>

      {/* Пагинация сверху */}
      <Box sx={{ mb: 2 }}>
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(event, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
          rowsPerPageOptions={[50, 100, 200, 500]}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </Box>

      {/* Таблица заблокированных IP */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px"></TableCell>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedIps.length === blockedIps.length && blockedIps.length > 0}
                  indeterminate={selectedIps.length > 0 && selectedIps.length < blockedIps.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>IP адрес</TableCell>
              <TableCell>Причина</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Подозрительных</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockedIps.map((ipData) => (
              <React.Fragment key={ipData.ip}>
                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleExpandIp(ipData.ip)}>
                  <TableCell>
                    <IconButton size="small">
                      {expandedIp === ipData.ip ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIps.includes(ipData.ip)}
                      onChange={() => handleSelectIp(ipData.ip)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {ipData.ip}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ipData.reason || 'Нет причины'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ipData.blocked_at === 'auto' ? 'Авто' : 'Ручная'}
                      color={ipData.blocked_at === 'auto' ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{ipData.suspicious_count || 0}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Редактировать">
                    <IconButton size="small" onClick={() => handleOpenEdit(ipData)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Разблокировать">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        if (window.confirm(`Разблокировать ${ipData.ip}?`)) {
                          try {
                            await monitoringAPI.unblockIp(ipData.ip);
                            onRefresh();
                          } catch (error) {
                            console.error('Error unblocking IP:', error);
                            alert('Ошибка при разблокировке IP');
                          }
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 0 }}>
                  <Collapse in={expandedIp === ipData.ip}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" gutterBottom>Подробная информация:</Typography>
                      <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                        <Typography variant="body2" gutterBottom>
                          <strong>IP адрес:</strong> <span style={{ fontFamily: 'monospace' }}>{ipData.ip}</span>
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Причина блокировки:</strong> {ipData.reason || 'Не указана'}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Тип блокировки:</strong> {ipData.blocked_at === 'auto' ? 'Автоматическая' : 'Ручная'}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Подозрительных активностей:</strong> {ipData.suspicious_count || 0}
                        </Typography>
                      </Paper>

                      {/* Последние запросы от этого IP */}
                      {ipLogs[ipData.ip] && ipLogs[ipData.ip].length > 0 && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Последние запросы (показаны до 10):
                          </Typography>
                          {ipLogs[ipData.ip].map((log, idx) => (
                            <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                              <Typography variant="body2" gutterBottom>
                                <strong>Время:</strong> {new Date(log.timestamp).toLocaleString('ru-RU')}
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                <strong>Запрос:</strong> <Chip label={log.method} size="small" sx={{ mr: 1 }} /> 
                                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{log.path}</span>
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                <strong>Статус:</strong> <Chip label={log.status_code} size="small" color={log.status_code >= 400 ? 'error' : 'success'} />
                              </Typography>
                              
                              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Заголовки:</Typography>
                              <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                                <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto', maxHeight: '300px' }}>
                                  {JSON.stringify(log.headers, null, 2)}
                                </pre>
                              </Paper>
                            </Paper>
                          ))}
                        </>
                      )}
                      
                      {ipLogs[ipData.ip] && ipLogs[ipData.ip].length === 0 && (
                        <Paper sx={{ p: 2, bgcolor: 'white' }}>
                          <Typography variant="body2" color="text.secondary">
                            Нет логов запросов для этого IP в последних 200 запросах
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
              </React.Fragment>
            ))}
            {blockedIps.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Нет заблокированных IP
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(event, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
          rowsPerPageOptions={[50, 100, 200, 500]}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
        />
      </TableContainer>

      {/* Диалог редактирования */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать блокировку</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="IP адрес"
              value={editingIp || ''}
              disabled
              fullWidth
            />
            <TextField
              label="Причина блокировки"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Тип блокировки</InputLabel>
              <Select
                value={editBlockedBy}
                onChange={(e) => setEditBlockedBy(e.target.value)}
                label="Тип блокировки"
              >
                <MenuItem value="manual">Ручная</MenuItem>
                <MenuItem value="auto">Автоматическая</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSaveEdit} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления IP */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить IP в блокировку</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="IP адреса (по одному на строку)"
              value={newIps}
              onChange={(e) => setNewIps(e.target.value)}
              multiline
              rows={5}
              fullWidth
              placeholder="192.168.1.1&#10;10.0.0.1&#10;172.16.0.1"
            />
            <TextField
              label="Причина блокировки"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleAddIps} variant="contained">Добавить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityManagement;
